import { Box, Card, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconCalendar, IconEye, IconUser } from '@tabler/icons-react';
import { labToRgb, rgbToHex } from 'utils/helper';
import CopyContentLink from '../copyContentLink/CopyContentLink';
import DateDisplay, { isDateString } from 'core/components/dateDisplay/DateDisplay';


const PaletteInfo = ({ 
    paletteData, 
    size = 'md', 
    showDetails = true, 
    showColorValues = true,
    showScanInfo = true,
    layout = 'vertical' // 'vertical' | 'horizontal' | 'compact'
}) => {
    if (!paletteData) {
        return null;
    }

    const {
        labL,
        labA,
        labB,
        r,
        g,
        b,
        crPart,
        crSort,
        scanDate,
        operator
    } = paletteData;

    // Use provided RGB values or convert from LAB if RGB not available
    const rgbValues = r && g && b ? 
        [parseInt(r), parseInt(g), parseInt(b)] : 
        labToRgb(parseFloat(labL), parseFloat(labA), parseFloat(labB));

    const hexColor = rgbToHex(rgbValues[0], rgbValues[1], rgbValues[2]);
    
    const swatchSize = size === 'sm' ? 24 : size === 'md' ? 32 : size === 'lg' ? 48 : 64;
    const styleId = crPart ? crPart.split('#')[2] : crPart;

    const ColorSwatch = () => (
        <Tooltip label={`${hexColor} | RGB(${Math.round(rgbValues[0])}, ${Math.round(rgbValues[1])}, ${Math.round(rgbValues[2])})`}>
            <Box
                sx={(theme) => ({
                    width: swatchSize,
                    height: swatchSize,
                    borderRadius: theme.radius.md,
                    backgroundColor: hexColor,
                    // border: `2px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                    
                    cursor: 'pointer',
                    boxShadow: theme.shadows.sm,
                    flexShrink: 0,
                    '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease',
                    }
                })}
            />
        </Tooltip>
    );

    const ColorValues = () => (
        <Stack spacing="xs" w="100%">
            <Group spacing="md" grow align="flex-start">
                <CopyContentLink align="start" allowItemClick={true} value={`L: ${parseFloat(labL).toFixed(1)} A: ${parseFloat(labA).toFixed(1)} B: ${parseFloat(labB).toFixed(1)}`}>
                    <Box>
                        <Text size="xs" color="dimmed" weight={500}>LAB</Text>
                        <Text size="sm" ff="monospace">
                            L: {parseFloat(labL).toFixed(1)}<br/>
                            A: {parseFloat(labA).toFixed(1)}<br/>
                            B: {parseFloat(labB).toFixed(1)}
                        </Text>
                    </Box>
                </CopyContentLink>
                <CopyContentLink align="start" allowItemClick={true} value={`R: ${Math.round(rgbValues[0])} G: ${Math.round(rgbValues[1])} B: ${Math.round(rgbValues[2])}`}>  
                <Box>
                    <Text size="xs" color="dimmed" weight={500}>RGB</Text>
                    <Text size="sm" ff="monospace">
                        R: {Math.round(rgbValues[0])}<br/>
                        G: {Math.round(rgbValues[1])}<br/>
                        B: {Math.round(rgbValues[2])}
                    </Text>
                </Box>
                </CopyContentLink>

                <CopyContentLink align="start" allowItemClick={true} value={hexColor.toUpperCase()}>
                <Box>
                    <Text size="xs" color="dimmed" weight={500}>HEX</Text>
                    <Text size="sm" ff="monospace" weight={500}>
                        {hexColor.toUpperCase()}
                    </Text>
                </Box>
                </CopyContentLink>
            </Group>
        </Stack>
    );

    const ScanInfo = () => (
        <Stack spacing={0} w="100%" pt={'sm'} sx={(theme) => ({borderTop: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : `1px solid ${theme.colors.gray[3]}`})}>
            <Group spacing="md" grow noWrap>
                <Group spacing="xs">
                    <IconCalendar size={14} />
                    <Box>
                        <Text size="xs" color="dimmed">Scan Date</Text>
                        <Text size="sm">
                            {isDateString(scanDate) ? <DateDisplay value={scanDate} /> : '-'}
                        </Text>
                    </Box>
                </Group>
                {operator && (
                    <Group spacing="xs">
                        <IconUser size={14} />
                        <Box>
                            <Text size="xs" color="dimmed">Operator</Text>
                            <Text size="sm">{operator}</Text>
                        </Box>
                    </Group>
                )}
                {crPart && (
                <Group spacing="xs">
                    <IconEye size={14} />
                    <Box>
                        <Text size="xs" color="dimmed">Reference</Text>
                        <Text size="sm" >
                            {styleId}
                        </Text>
                    </Box>
                </Group>
            )}
            </Group>
            
        </Stack>
    );

    if (layout === 'compact') {
        return (
            <Group spacing="sm" align="flex-start">
                <ColorSwatch />
                <Stack spacing="xs" sx={{ flex: 1 }}>
                    <Text size="sm" ff="monospace" weight={500}>
                        {hexColor.toUpperCase()}
                    </Text>
                    {operator && (
                        <Text size="xs" color="dimmed">
                            by {operator}
                        </Text>
                    )}
                </Stack>
            </Group>
        );
    }

    if (layout === 'horizontal') {
        return (
            <Paper withBorder p="md" style={{width: '100%'}}>
                <Group spacing="lg" align="flex-start">
                    <ColorSwatch />
                    <Stack spacing="md" sx={{ flex: 1 }}>
                        {showColorValues && <ColorValues />}
                        {showScanInfo && <ScanInfo />}
                    </Stack>
                </Group>
            </Paper>
        );
    }

    // Default vertical layout
    return (
        <Card p="md" withBorder>
            <Stack spacing="md">
                <Group position="center">
                    <ColorSwatch />
                </Group>
                
                {showColorValues && <ColorValues />}
                
                {showScanInfo && <ScanInfo />}
            </Stack>
        </Card>
    );
};

export default PaletteInfo; 